// Who gets geometry packing, and for how long.
//
// Packing - per-tile batching and merged singleton meshes - changes how
// every element in the model is submitted to the GPU. It is worth about
// a quarter fewer draw calls, which reads as smoother movement rather
// than faster loading, and it has one known gap: an export published
// before the exporter wrote its size ordering gets no hover silhouette
// on a merged element.
//
// BATCHING IS THE DEFAULT (owner, 2026-09-17). With everything in view
// loading, SpaceX drew one pool per element - 7,434 pools for 13,507
// instances, ~2 each - so instancing was buying nothing and every element
// paid a draw call of its own. Per-tile batching folds those rare rows
// into one draw per tile. Merged singleton meshes stay opt-in: they carry
// the hover-silhouette gap on older exports.
//
// Any browser can still choose for itself:
//
//   ?packing=all          batching and merges, this browser only
//   ?packing=merge        merged singleton meshes only (no batching)
//   ?packing=batching     per-tile batching only (the default)
//   ?packing=off          neither - the renderer before 2026-09-17
//
// The choice is remembered in this browser, so it survives a reload and
// moving around the app after the parameter has gone from the address
// bar. It is per browser and per device: nobody else is affected, and it
// never reaches the server.
//
// `window.BIDLIGHT_BATCHING` / `window.BIDLIGHT_MERGE` still win over
// all of it. A headless capture sets those before opening a model, and
// that is how the on/off comparisons are measured.
const STORE_KEY = 'bidlight.packing';
const KINDS = ['batching', 'merge'];

const OFF = new Set(['off', 'none', 'no', '0', 'false']);
const ALL = new Set(['all', 'both', 'on', '1', 'true', 'yes']);

// A parameter or a stored value into the set of kinds it names.
function parseSpec(spec) {
  const parts = String(spec || '').toLowerCase().split(/[,+\s]+/).filter(Boolean);
  if (!parts.length) return null;
  if (parts.some((p) => OFF.has(p))) return new Set();
  const out = new Set();
  for (const p of parts) {
    if (ALL.has(p)) { KINDS.forEach((k) => out.add(k)); continue; }
    if (KINDS.includes(p)) out.add(p);
    // 'merged', 'merges', 'batch' - say what was meant, not what was typed.
    else if (p.startsWith('merge')) out.add('merge');
    else if (p.startsWith('batch')) out.add('batching');
  }
  // Nothing recognisable is no choice at all (the default stands), not
  // "off" - only an explicit off word turns packing off.
  return out.size ? out : null;
}

function readStore() {
  try {
    return localStorage.getItem(STORE_KEY);
  } catch (e) {
    // Private windows and blocked site data both throw here. No stored
    // choice is the same as no choice.
    return null;
  }
}

function writeStore(spec) {
  try {
    if (spec === null) localStorage.removeItem(STORE_KEY);
    else localStorage.setItem(STORE_KEY, spec);
  } catch (e) { /* nothing to remember it in */ }
}

// Resolved once per page load, because the loader asks per model and the
// answer must not change between two models on one page.
let resolved = null;

export function resolvePacking(search = null) {
  const query = search !== null ? search
    : (typeof window !== 'undefined' && window.location ? window.location.search : '');
  let spec = null;
  try {
    spec = new URLSearchParams(query || '').get('packing');
  } catch (e) { spec = null; }

  // The address bar is an instruction, and it is also a change of mind:
  // it replaces whatever this browser was remembering.
  if (spec !== null) {
    const wanted = parseSpec(spec);
    // An explicit "off" is remembered as "off": with batching on by
    // default, forgetting the choice would turn it back on.
    writeStore(wanted ? (wanted.size ? [...wanted].join(',') : 'off') : null);
    return wanted || defaultPacking();
  }
  return parseSpec(readStore()) || defaultPacking();
}

// What a browser that has never chosen gets.
export function defaultPacking() {
  return new Set(['batching']);
}

export function packingEnabled(kind) {
  if (typeof window !== 'undefined') {
    // The capture harness's override, unchanged.
    if (kind === 'batching' && window.BIDLIGHT_BATCHING === true) return true;
    if (kind === 'merge' && window.BIDLIGHT_MERGE === true) return true;
  }
  if (!resolved) {
    resolved = resolvePacking();
    // Said once, and only when something is on: whoever turned it on is
    // about to judge what they see, and needs to know it took effect.
    // Also how they turn it off, because they will be looking at the
    // model rather than at this conversation.
    if (resolved.size && typeof console !== 'undefined') {
      console.log(
        `[bidlight] geometry packing ON for this browser: ${[...resolved].join(', ')}. `
        + 'Add ?packing=off to the address to turn it off.'
      );
    }
  }
  return resolved.has(kind);
}

// Tests, and a second model on one page.
export function _reset() { resolved = null; }

export default packingEnabled;
